import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import { PageHero, CtaBand, CheckList } from "@/components/blocks";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/data/site";
import api from "@/lib/api";
import { MapPin, Phone, Smartphone, Mail, FileBadge, Users, Briefcase, Clock, GraduationCap, CalendarClock, SendHorizontal } from "lucide-react";
import { JobApplicationModal } from "@/components/JobApplicationModal";
import { breadcrumbJsonLd } from "@/lib/seo";

const SECTIONS = [
  {
    title: "Who We Are",
    text: "Cherians Tech Solutions Private Limited is a technology solutions company based in Mallappally, Kerala. CTS has been serving customers under the CTS name since 2019. Our business experience, however, goes back to 2014, when we began operations under an earlier name.",
  },
  {
    title: "What We Do",
    text: "We provide computer products, assembled PCs, printers, toners, networking solutions, CCTV installation and technical services — with on-site support within approximately 15–20 km of our location.",
  },
  {
    title: "Our Approach",
    text: "Our approach is simple: understand what the customer needs, recommend a practical solution, install it properly and provide support when required.",
  },
  {
    title: "Who We Serve",
    text: "We work with individuals, homes, small businesses, offices, hospitals, banks and other institutions — anyone who depends on technology that needs to work reliably.",
  },
];

const WHY_WORK = [
  "Work on real customer requirements — computers, printers, networking and CCTV",
  "Hands-on experience across products, installation and support",
  "A local business with direct customer relationships",
  "A practical, straightforward way of working",
];

const VacancyCard = ({ v, onApply }) => {
  const displayTitle = v.position === "Other" && v.custom_title ? v.custom_title : v.position;
  return (
    <div className="rounded-xl border border-cts-line bg-white p-6" data-testid={`vacancy-card-${v.id}`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h3 className="font-heading font-bold text-cts-ink text-lg" data-testid={`vacancy-title-${v.id}`}>{displayTitle}</h3>
        <button onClick={() => onApply(v)} data-testid={`vacancy-apply-btn-${v.id}`} className="btn-primary !min-h-[38px] !py-1.5 !px-4 !text-xs shrink-0">
          <SendHorizontal size={14} /> APPLY NOW
        </button>
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-cts-charcoal/80 mb-3">
        {v.location && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-cts-red" /> {v.location}</span>}
        {v.employment_type && <span className="flex items-center gap-1.5"><Clock size={14} className="text-cts-red" /> {v.employment_type}</span>}
        {v.experience_required && <span className="flex items-center gap-1.5"><GraduationCap size={14} className="text-cts-red" /> {v.experience_required}</span>}
        {v.closing_date && <span className="flex items-center gap-1.5"><CalendarClock size={14} className="text-cts-red" /> Apply by {new Date(v.closing_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>}
      </div>
      {v.job_description && <p className="lead-text mb-3">{v.job_description}</p>}
      {v.qualifications && (
        <div className="mb-3">
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-cts-ink mb-1">Qualifications / Skills</p>
          <p className="text-sm text-cts-charcoal whitespace-pre-line">{v.qualifications}</p>
        </div>
      )}
      {v.responsibilities && (
        <div className="mb-3">
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-cts-ink mb-1">Key Responsibilities</p>
          <p className="text-sm text-cts-charcoal whitespace-pre-line">{v.responsibilities}</p>
        </div>
      )}
      {v.how_to_apply && (
        <div className="rounded-md bg-cts-soft px-4 py-3 mt-2">
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-cts-ink mb-1">How to Apply</p>
          <p className="text-sm text-cts-charcoal whitespace-pre-line">{v.how_to_apply}</p>
        </div>
      )}
    </div>
  );
};

const CurrentVacancies = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingTo, setApplyingTo] = useState(null);

  useEffect(() => {
    api.get("/vacancies").then(({ data }) => setVacancies(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <Reveal>
      <h2 className="h3-display mb-4">Current Vacancies</h2>
      {vacancies.length === 0 ? (
        <div className="rounded-xl border border-cts-line bg-cts-soft p-6" data-testid="no-current-vacancies">
          <p className="font-heading font-bold text-cts-ink">No current vacancies</p>
          <p className="text-sm text-cts-charcoal mt-1">We do not have any open positions at present. Please check back for future opportunities.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="current-vacancies-list">
          {vacancies.map((v) => <VacancyCard key={v.id} v={v} onApply={setApplyingTo} />)}
        </div>
      )}
      <JobApplicationModal vacancy={applyingTo} open={!!applyingTo} onClose={() => setApplyingTo(null)} />
    </Reveal>
  );
};

export default function About() {
  return (
    <>
      <Seo
        title="About CTS | Technology Company in Mallappally"
        siteName={COMPANY.brand}
        description="Cherians Tech Solutions Private Limited is a technology solutions company in Mallappally, Kerala — serving customers under the CTS name since 2019, with business experience going back to 2014."
        jsonLd={breadcrumbJsonLd([{ label: "About" }])}
      />
      <PageHero
        eyebrow="ABOUT US"
        title="About"
        intro="Technology. Solutions. Support. — a local technology partner for individuals, businesses and institutions."
        crumbs={[{ label: "About" }]}
      />
      <section className="section-pad bg-white">
        <div className="container-site grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-10">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <h2 className="h3-display mb-3">{s.title}</h2>
                <p className="lead-text">{s.text}</p>
              </Reveal>
            ))}
            <Reveal>
              <h2 className="h3-display mb-3">Our Team</h2>
              <div className="rounded-xl border border-cts-line bg-cts-soft p-7 flex items-start gap-4" data-testid="about-team-card">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-white border border-cts-line shrink-0">
                  <Users size={22} strokeWidth={1.75} className="text-cts-red" />
                </span>
                <p className="lead-text">
                  CTS is a small, hands-on team based in Mallappally. When a job needs specialized
                  skills, we bring in experienced technicians to get it done properly.
                </p>
              </div>
            </Reveal>
            <Reveal>
              <h2 className="h3-display mb-3">Careers at CTS</h2>
              <p className="lead-text mb-5">
                We occasionally look for people who enjoy practical, hands-on technology work — from
                assembling computers to networking and installations. If that sounds like you, send
                your details and a short note about yourself.
              </p>
              <a href={`mailto:${COMPANY.email}?subject=Careers at CTS`} data-testid="careers-email-btn" className="btn-outline">
                <Mail size={16} /> EMAIL YOUR DETAILS
              </a>
            </Reveal>
            <CurrentVacancies />
            <Reveal>
              <h2 className="h3-display mb-4 flex items-center gap-3">
                <Briefcase size={22} strokeWidth={1.75} className="text-cts-red" /> Why Work With CTS?
              </h2>
              <CheckList items={WHY_WORK} />
            </Reveal>
          </div>
          <Reveal delay={120}>
            <aside className="rounded-xl border border-cts-line bg-cts-soft p-7 h-fit lg:sticky lg:top-28" data-testid="about-company-info">
              <p className="eyebrow mb-2">COMPANY INFORMATION</p>
              <h2 className="font-heading font-bold text-cts-ink text-lg mb-5">Company Information</h2>
              <ul className="space-y-4 text-[15px] text-cts-charcoal">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-cts-red mt-0.5 shrink-0" />
                  <address className="not-italic leading-relaxed">
                    <strong className="text-cts-ink">{COMPANY.name}</strong>
                    <br />
                    {COMPANY.addressLines.map((l) => (
                      <span key={l}>{l}<br /></span>
                    ))}
                  </address>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-cts-red shrink-0" />
                  <a href={COMPANY.phoneHref} className="hover:text-cts-red transition-colors">{COMPANY.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Smartphone size={18} className="text-cts-red shrink-0" />
                  <a href={COMPANY.mobileHref} className="hover:text-cts-red transition-colors">{COMPANY.mobile}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-cts-red shrink-0" />
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-cts-red transition-colors break-all">{COMPANY.email}</a>
                </li>
                <li className="flex items-center gap-3">
                  <FileBadge size={18} className="text-cts-red shrink-0" />
                  <span>GST: {COMPANY.gst}</span>
                </li>
              </ul>
            </aside>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
